import React from "react";

import { cn } from "../../../components/common/u1/cn";
import { Breadcrumb } from "../../../components/common/u1/Breadcrumb";
import { PageContainer } from "../../../components/common/u1/PageContainer";
import Header from "../../../components/public/Header";
import Footer from "../../../components/public/Footer";
import { PlanChoices } from "../../../components/public/Payment/u1/PlanChoices";
import { OrderSummaryPanel } from "../../../components/public/Payment/u1/OrderSummaryPanel";
import { usePlanSelection } from "../../../components/public/Payment/u1/usePlanSelection";
import { PROFILE_WINDOW_DAYS } from "../../../constants/webProfilePricing";
import "../../../components/common/u1/tokens.css";

/* u1 Buy a Web Profile — step 1, /u1/plans.
 *
 * Visual uplift only. Every rule lives in usePlanSelection, transcribed from
 * components/public/Payment/PaymentPlans.jsx: the same TIP-530 guard, the same
 * TIP-493 (W3) fresh-status fetch, the same stale-mode cleanup, and the same
 * handoff to the payment step. The live /plans route still renders the
 * original.
 *
 * The summary panel is the one built for the payment step — the same order,
 * shown at both stages, so it is one component rather than two that drift.
 */
export default function Plans() {
  const page = usePlanSelection();

  /* TIP-530 — the guard's effect is already redirecting; rendering a payable
     step for the frame in between is what it exists to prevent. */
  if (page.blocked) return null;

  return (
    <div className="u1-root min-h-screen w-full bg-[var(--bg)]">
      {/* This route sits outside RootLayout, so it brings its own bar. */}
      <Header sticky wide />

      <PageContainer className="py-6">
        <Breadcrumb
          items={[
            { label: "Home", to: "/" },
            ...(page.company?.registered_company_name
              ? [{ label: page.company.registered_company_name }]
              : []),
            { label: "Buy a Web Profile" },
          ]}
        />

        <header className="mt-4 max-w-[640px]">
          <p className="m-0 text-[11.5px] font-bold uppercase tracking-[.13em] text-[var(--coral)] font-sans">
            Step 1 of 2
          </p>
          <h1 className="mb-0 mt-2 text-[26px] font-bold leading-[1.2] text-[var(--ink)] font-sans sm:text-[30px]">
            Buy a Web Profile
          </h1>
          <p className="mb-0 mt-2.5 text-[13.5px] leading-[1.55] text-[var(--muted)] font-sans">
            Unlock {page.companyName}&apos;s full profile for {PROFILE_WINDOW_DAYS}{" "}
            days. {page.intro}
          </p>
        </header>

        <div
          className={cn(
            "mt-6 grid grid-cols-1 items-start gap-6",
            "md:grid-cols-[minmax(0,1fr)_340px]"
          )}
        >
          <PlanChoices
            isMember={page.isMember}
            canJoin={page.canJoin}
            mode={page.mode}
            setMode={page.setMode}
            plan={page.plan}
            selectBundlePlan={page.selectBundlePlan}
            onContinue={page.handleContinue}
            onCancel={page.handleCancel}
          />

          <OrderSummaryPanel
            company={page.company}
            summary={page.summary}
            upsell={page.upsell}
            /* Transcribed: here the upsell selects the bundle card rather than
               navigating anywhere — the choice is on this very screen. */
            onSwitchToMembership={() => page.setMode("bundle")}
          />
        </div>
      </PageContainer>

      <Footer />
    </div>
  );
}
